import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/client';

export type ResourceStatus = 'loading' | 'ready' | 'error';

export interface Resource<T> {
  /** The last successful body, or null before the first success. */
  data: T | null;
  status: ResourceStatus;
  /** Persian message when status==='error'. */
  error: string | null;
  /** true during a soft refresh (data stays on screen). */
  refreshing: boolean;
  /** Re-fetch. `reload(true)` keeps current data visible (soft refresh). */
  reload: (soft?: boolean) => void;
}

/**
 * The ONE place an on-mount GET read should live.
 *
 * Replaces the copy-pasted `useState(null) → useEffect → api().then(r => r.ok && setX)`
 * idiom (~30 screens) and closes the whole "stuck on a spinner / blank table forever"
 * class of bug: a failed read lands in `status: 'error'` with a retry, never a frozen
 * loading string. A monotonic request id makes stale/superseded/after-unmount responses
 * no-ops, so fast tab-switching can't setState an unmounted screen or apply an out-of-order
 * result. Pass `deps` to re-fetch when a key changes (e.g. a selected month).
 */
export function useApiResource<T>(path: string, deps: readonly unknown[] = []): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [status, setStatus] = useState<ResourceStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const reqId = useRef(0);

  const load = useCallback(
    async (soft = false) => {
      const id = ++reqId.current;
      if (soft) setRefreshing(true);
      else setStatus('loading');
      const r = await api<T>(path);
      if (id !== reqId.current) return; // superseded by a newer load, or unmounted → ignore
      if (r.ok) {
        setData(r.body as T);
        setError(null);
        setStatus('ready');
      } else {
        setError(
          (r.body as { message?: string } | undefined)?.message ?? 'خطا در بارگذاری اطلاعات',
        );
        setStatus('error');
      }
      setRefreshing(false);
    },
    // deps intentionally spread so callers can re-fetch on a key change
    [path, ...deps],
  );

  useEffect(() => {
    void load();
    return () => {
      reqId.current++; // invalidate any in-flight request on unmount / dep change
    };
  }, [load]);

  return { data, status, error, refreshing, reload: (soft) => void load(soft) };
}
